import { type DeleteDependency } from "../../components/ConfigListShell/ConfigDeleteDialog";
import { CupDependencies, CupResponse } from "../../domain/cup.interface";

/**
 * SIMS-8850 — what the delete confirmation says, kept out of the container so it can be tested
 * without mounting a screen.
 *
 * COUNTS ONLY. `visits` is derived from clinical visit records, so only the aggregate crosses
 * this boundary — no visit, enrollment or participant identifier (NFR-4, compliance C-2,
 * TestRail C17874). Nothing here accepts an identifier, so there is no path by which one could
 * reach the dialog.
 */

/**
 * The recovery, corrected against FR-42.
 *
 * The design words it "…and remove it from treatment plan selection", which reads as though going
 * Inactive takes the cup out of the plans that use it. FR-42 says the opposite: "Treatment plans
 * already holding it keep it, and it remains visible and marked inactive within that
 * configuration" — and the design's own markup agrees at L848, "Kept because this plan already
 * uses it". An administrator blocked by treatment plans alone who reads the original sentence
 * would set the cup Inactive, retry, and be blocked by the same plans.
 *
 * Inactive is still the right advice: it is the alternative to deleting, not a way to lift the
 * block — recorded visits never go away either. The sentence now says which part it changes.
 *
 * This is second-reader diff D5, bundled into Q6 and never answered. Resolved here against FR-42
 * rather than left to render a misleading instruction; flagged on the PR for the PO.
 */
export const cupDeleteBlockedSuggestion =
	"Set it to Inactive instead — that stops new visits and keeps it out of new treatment plans. Plans already using it keep it, marked inactive.";

/**
 * AC1 — the allowed state restates BOTH zero conditions in words, which is why AC1's precondition
 * is "no visits AND no plan" rather than either alone.
 */
export const cupDeleteAllowedBody = (cup: CupResponse): string =>
	`${cupDeleteName(cup)} will be permanently deleted. This cannot be undone. No treatment plan uses it and no visit has been recorded with it.`;

/** The design identifies the cup by name and vendor, since a name is unique only within one. */
export const cupDeleteName = (cup: CupResponse): string =>
	`${cup.name} (${cup.vendor})`;

/**
 * A ZERO COUNT IS NOT A DEPENDENCY, and this is the one place the build departs from the design.
 *
 * The Hi-Fi's `delBody` is a single interpolated sentence emitting both numbers unconditionally,
 * so its own seed data renders "is used by 0 treatment plans and has 134 recorded visits" —
 * a zero stated as a blocking reason, when AC2 and AC3 each ask for the count that applies.
 *
 * The decisive argument is FOUNDATION, not the case wording. SIMS-8647 already met this exact
 * defect on payers, named it in `ConfigDeleteDialog`'s own docblock ("configured on 0 programs and
 * has 412 registered members"), and settled it under C17521 by having the dialog take a LIST
 * rather than two named fields. Omitting the zero is simply what that list does; emitting one
 * would mean bypassing the shared component, and cups reading differently from payers is a
 * difference the administrator can see and nobody asked for.
 *
 * C17872 and C17875 saying "the count", singular, corroborates it, though that is a reading of
 * phrasing rather than a specification. C17878 does NOT support it and an earlier version of this
 * comment wrongly claimed it did: "3 treatment plans" becoming "0 treatment plans" is also the
 * message updating. The objection to the Hi-Fi is the zero stated as a reason, not a failure to
 * update.
 *
 * FR-41's "the message states both counts" is fairly read as describing the AC4 case, which is
 * implemented and covered by C17877.
 *
 * Plans lead, then visits, following the design's sentence order.
 */
export const cupDeleteDependencies = (
	counts: CupDependencies | undefined,
): DeleteDependency[] => {
	if (!counts) {
		return [];
	}
	const deps: DeleteDependency[] = [];
	if (counts.treatmentPlans > 0) {
		deps.push({
			kind: "treatmentPlans",
			count: counts.treatmentPlans,
			label: "treatment plans",
		});
	}
	if (counts.visits > 0) {
		deps.push({
			kind: "visits",
			count: counts.visits,
			label: "recorded visits",
		});
	}
	return deps;
};

/**
 * The counts a DELETE was refused with.
 *
 * The dialog's counts were fetched when it opened and can be stale by the time Delete is clicked
 * — a visit recorded in between is precisely the race the server guard exists for. When the
 * server refuses, its numbers replace the fetched ones rather than the user seeing a bare
 * failure beside a dialog still claiming the cup is free.
 *
 * Reads the UNWRAPPED body first: `cupService.deleteCup` rejects with `err.response?.data || err`,
 * so the axios envelope is already gone. The nested shape is accepted too, for a caller that has
 * not unwrapped.
 */
export const dependenciesFromConflict = (
	error: unknown,
): DeleteDependency[] | null => {
	const e = error as {
		statusCode?: number;
		visits?: unknown;
		treatmentPlans?: unknown;
		response?: {
			status?: number;
			data?: { visits?: unknown; treatmentPlans?: unknown };
		};
	};
	const status = e?.statusCode ?? e?.response?.status;
	if (status !== 409) {
		return null;
	}
	const body = e?.response?.data ?? e;
	const visits = body?.visits;
	const treatmentPlans = body?.treatmentPlans;
	// A 409 without the two numbers is the name-conflict 409 from SIMS-8962, or a shape we do not
	// recognise. Either way it is not a dependency refusal, so it falls through to the shell's
	// generic message rather than being rendered as "0 and 0", which would read as "safe".
	if (typeof visits !== "number" || typeof treatmentPlans !== "number") {
		return null;
	}
	const deps = cupDeleteDependencies({ visits, treatmentPlans });
	return deps.length > 0 ? deps : null;
};
